import axios from 'axios';
import { store } from './redux/store';
import { setWhatsAppCode } from './redux/features/user/userSlice';
import {
  get_whatsapp_verification_status,
  setWhatsAppCodeVerificationStatus,
} from './redux/features/sockets/socketslice';
import { initializeSocketConnection } from './redux/socketEvents';
import { toast } from 'react-toastify';
import { setJwtToken } from './redux/features/user/userSlice';

export const apiUrl = process.env.REACT_APP_API_URL;

// Error handling helper
const handleApiError = (error, context) => {
  let errorDetails = {
    message: 'Unknown API error',
    context,
    timestamp: new Date().toISOString(),
  };

  if (error.response) {
    // Server responded with error status (4xx/5xx)
    errorDetails = {
      ...errorDetails,
      message: error.response.data?.message || 'Server error',
      status: error.response.status,
      data: error.response.data,
    };
  } else if (error.request) {
    // Request made but no response received
    errorDetails = {
      ...errorDetails,
      message: 'Network error: No response from server',
      request: error.request,
    };
  } else {
    // Request setup error
    errorDetails = {
      ...errorDetails,
      message: `Request error: ${error.message}`,
    };
  }

  console.error(`API Error [${context}]:`, errorDetails);
  return errorDetails;
};

// API functions
export const customSignupandLogin = async (phone) => {
  try {
    const response = await axios.get(`${apiUrl}/auth/get-otp/${phone}`);
    return response;
  } catch (error) {
    throw handleApiError(error, 'customSignupandLogin');
  }
};

export const setcustomJWT = async (phone, otp) => {
  try {
    const response = await axios.get(`${apiUrl}/auth/get-token/${phone}/${otp}`);
    return response;
  } catch (error) {
    throw handleApiError(error, 'setcustomJWT');
  }
};

export const checkUserAccount = async (token) => {
  try {
    const response = await axios.get(`${apiUrl}/auth/info-check`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'checkUserAccount');
  }
};

export const signUpUser = async (token, inviteRFCode) => {
  try {
    const response = await axios.post(
      `${apiUrl}/auth/signup/${inviteRFCode ? `${inviteRFCode}` : 'user'}`,
      null,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'signUpUser');
  }
};

export const getOTP = async (token) => {
  try {
    await axios.get(`${apiUrl}/tx/get-otp`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    throw handleApiError(error, 'getOTP');
  }
};

export const getAgentOTP = async (token, phoneNumber) => {
  try {
    await axios.get(`${apiUrl}/agent/tx/get-otp/${phoneNumber}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    throw handleApiError(error, 'getAgentOTP');
  }
};

export const updateAgentStatus = async (token, status) => {
  try {
    const response = await axios.put(
      `${apiUrl}/agent/update-availability/${status}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'updateAgentStatus');
  }
};

export const getUserProfile = async (token) => {
  try {
    const response = await axios.get(`${apiUrl}/auth/get-account`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'getUserProfile');
  }
};

export const updateUserProfile = async (userInfo, token) => {
  try {
    const response = await axios.post(
      `${apiUrl}/auth/personal-info-edit-request`,
      userInfo,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'updateUserProfile');
  }
};

export const getUserBalance = async (token) => {
  try {
    const response = await axios.get(`${apiUrl}/tx/get-balance`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'getUserBalance');
  }
};

export const setUserDevice = async (code, token) => {
  try {
    const response = await axios.post(
      `${apiUrl}/auth/save-device`,
      { code },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'setUserDevice');
  }
};

export const getUserDevice = async (token) => {
  try {
    const response = await axios.get(`${apiUrl}/auth/get-device`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'getUserDevice');
  }
};

export const addEmail = async (email, token, subscribe) => {
  try {
    const response = await axios.post(
      `${apiUrl}/account/add-email/${email}/${subscribe}`,
      null,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'addEmail');
  }
};

export const verificationEmail = async (userToken, emailToken) => {
  try {
    const response = await axios.get(`${apiUrl}/account/verify-email/${emailToken}`, {
      headers: { Authorization: `Bearer ${userToken}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'verificationEmail');
  }
};

export const updateKYC = async (step, stepData, token) => {
  try {
    const response = await axios.put(
      `${apiUrl}/auth/update-kyc/${step}`,
      stepData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'updateKYC');
  }
};

export const submitKYC = async (data, token) => {
  try {
    const response = await axios.post(
      `${apiUrl}/auth/submit-kyc`,
      data,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'submitKYC');
  }
};

export const getTXlist = async (token, period = 3) => {
  try {
    const response = await axios.get(`${apiUrl}/tx/activity?period=${period}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'getTXlist');
  }
};

export const getTXlast = async (token) => {
  try {
    const response = await axios.get(`${apiUrl}/tx/latest-tx`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'getTXlast');
  }
};

export const swapCurrency = async (swap, userToken, deviceToken) => {
  try {
    const response = await axios.post(
      `${apiUrl}/tx/swap`,
      swap,
      { headers: { 
        Authorization: `Bearer ${userToken}`, 
        device: deviceToken 
      } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'swapCurrency');
  }
};

export const sendCurrency = async (data, userToken, deviceToken, currency) => {
  try {
    const response = await axios.post(
      `${apiUrl}/tx/${currency}/transfer`,
      data,
      { headers: { 
        Authorization: `Bearer ${userToken}`,
        device: deviceToken
      } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'sendCurrency');
  }
};

export const withdrawCurrency = async (data, userToken, deviceToken, currency) => {
  try {
    const response = await axios.post(
      `${apiUrl}/tx/${currency}/withdraw`,
      data,
      { headers: { 
        Authorization: `Bearer ${userToken}`, 
        device: deviceToken 
      } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'withdrawCurrency');
  }
};

export const getAgentsList = async (token, online) => {
  try {
    const response = await axios.get(
      `${apiUrl}/agent/get-agents${online ? '?online=true' : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'getAgentsList');
  }
};

export const getAgentsSearch = async (token, searchValue, online = false, page = 1, limit = 5) => {
  try {
    const response = await axios.get(
      `${apiUrl}/agent/search/${searchValue}?online=${online}&page=${page}&limit=${limit}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'getAgentsSearch');
  }
};

export const getUserBanner = async (token) => {
  try {
    const response = await axios.get(`${apiUrl}/users/market-banner`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'getUserBanner');
  }
};

export const getContacts = async (token, page = 1, limit = 5, filter = 'all') => {
  try {
    const response = await axios.get(
      `${apiUrl}/contact/get-contacts/${filter}?page=${page}&limit=${limit}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'getContacts');
  }
};

export const updateFavouriteStatus = async (token, accountId, action) => {
  try {
    const response = await axios.put(
      `${apiUrl}/contact/update-favourite/${accountId}/${action}`,
      null,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'updateFavouriteStatus');
  }
};

export const getUsers = async (token, page, limit, value) => {
  try {
    const response = await axios.get(
      `${apiUrl}/users/search/users/${value}?page=${page}&limit=${limit}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'getUsers');
  }
};

export const getContactsSearch = async (token, page, limit, filter, value) => {
  try {
    const response = await axios.get(
      `${apiUrl}/contact/search/${filter}/${value}?page=${page}&limit=${limit}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'getContactsSearch');
  }
};

export const getTransactionDetails = async (token, transactionId) => {
  try {
    const response = await axios.get(`${apiUrl}/tx/transaction/${transactionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'getTransactionDetails');
  }
};

export const updateNotification = async (token, notificationID, action) => {
  try {
    const response = await axios.put(
      `${apiUrl}/notifications/update-read/${notificationID}/${action}`,
      null,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'updateNotification');
  }
};

export const deleteNotification = async (token, notificationID) => {
  try {
    const response = await axios.put(
      `${apiUrl}/notifications/delete/${notificationID}`,
      null,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'deleteNotification');
  }
};

export const getAvatar = async (url) => {
  try {
    if (!url) return null;
    const response = await axios.get(url, { responseType: 'blob' });
    return URL.createObjectURL(new Blob([response], { type: response.headers['content-type'] }));
  } catch (error) {
    console.error('Error fetching image:', error);
    return null;
  }
};

export const updateAvatar = async (data, userToken) => {
  try {
    const response = await axios.put(
      `${apiUrl}/account/edit-avatar`,
      data,
      { headers: { Authorization: `Bearer ${userToken}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'updateAvatar');
  }
};

export const deleteAvatar = async (token) => {
  try {
    const response = await axios.delete(`${apiUrl}/account/delete-avatar`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'deleteAvatar');
  }
};

// export const getNotifications = async (token) => {
//   try {
//     const response = await axios.get(`${apiUrl}/notifications/get-notifications`, {
//       headers: { Authorization: `Bearer ${token}` },
//     });
//     return response;
//   } catch (error) {
//     throw handleApiError(error, 'getNotifications');
//   }
// };

// export const activateSSP = async (token, value) => {
//   try {
//     const response = await axios.post(
//       `${apiUrl}/account/activate-ssp/${value}`,
//       null,
//       { headers: { Authorization: `Bearer ${token}` } }
//     );
//     return response;
//   } catch (error) {
//     throw handleApiError(error, 'activateSSP');
//   }
// };

export const getSettings = async (token) => {
  try {
    const response = await axios.get(`${apiUrl}/account/get-settings`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'getSettings');
  }
};

export const logOutAllDevices = async (userToken) => {
  try {
    const response = await axios.put(
      `${apiUrl}/account/logout-devices`,
      null,
      { headers: { Authorization: `Bearer ${userToken}` } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'logOutAllDevices');
  }
};

// AGENTS ENDPOINTS
export const deleteAccount = async (token, reason) => {
  try {
    const response = await axios.delete(`${apiUrl}/auth/delete-account?reason=${reason}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'deleteAccount');
  }
};

export const createRemit = async (token, deviceJwt, data) => {
  try {
    const response = await axios.post(
      `${apiUrl}/agent/request-id`,
      data,
      { headers: { 
        Authorization: `Bearer ${token}`, 
        device: deviceJwt 
      } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'createRemit');
  }
};

export const submitSender = async (token, deviceJwt, remitID, data) => {
  try {
    const response = await axios.post(
      `${apiUrl}/agent/tx/submit-sender/${remitID}`,
      data,
      { headers: { 
        Authorization: `Bearer ${token}`, 
      //   device: deviceJwt 
      } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'submitSender');
  }
};

export const submitReceiver = async (token, deviceJwt, remitID, data) => {
  try {
    const response = await axios.post(
      `${apiUrl}/agent/tx/submit-receiver/${remitID}`,
      data,
      { headers: { 
        Authorization: `Bearer ${token}`, 
      //   device: deviceJwt 
      } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'submitReceiver');
  }
};

export const completeFlowRemit = async (token, deviceJwt, txID, data) => {
  try {
    const response = await axios.post(
      `${apiUrl}/agent/tx/complete-remit/${txID}`,
      data,
      { headers: { 
        Authorization: `Bearer ${token}`, 
        device: deviceJwt 
      } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'completeFlowRemit');
  }
};

export const assignAgent = async (token, deviceJwt, remitID, agent) => {
  try {
    const response = await axios.put(
      `${apiUrl}/agent/tx/assign/${remitID}/${agent}`,
      {},
      { headers: { 
        Authorization: `Bearer ${token}`, 
        device: deviceJwt 
      } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'assignAgent');
  }
};

export const acceptRemit = async (txId, token, deviceJwt) => {
  try {
    const response = await axios.put(
      `${apiUrl}/agent/tx/accept/${txId}`,
      {},
      { headers: { 
        Authorization: `Bearer ${token}`, 
        device: deviceJwt 
      } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'acceptRemit');
  }
};

export const declineRemit = async (txId, token, deviceJwt, reason) => {
  try {
    const response = await axios.put(
      `${apiUrl}/agent/tx/decline/${txId}`,
      { reason },
      { headers: { 
        Authorization: `Bearer ${token}`, 
        device: deviceJwt 
      } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'declineRemit');
  }
};

export const completeRemit = async (txId, token, deviceJwt) => {
  try {
    const response = await axios.put(
      `${apiUrl}/agent/tx/complete/${txId}`,
      {},
      { headers: { 
        Authorization: `Bearer ${token}`, 
        device: deviceJwt 
      } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'completeRemit');
  }
};

export const addRemitToPool = async (txId, token, deviceJwt) => {
  try {
    const response = await axios.put(
      `${apiUrl}/agent/tx/assign-pool/${txId}/`,
      {},
      { headers: { 
        Authorization: `Bearer ${token}`, 
        device: deviceJwt 
      } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'addRemitToPool');
  }
};

export const cancelRemit = async (txId, token, deviceJwt, code, reason) => {
  try {
    const response = await axios.post(
      `${apiUrl}/agent/tx/cancel/${txId}`,
      { code, reason },
      { headers: { 
        Authorization: `Bearer ${token}`, 
        device: deviceJwt 
      } }
    );
    return response;
  } catch (error) {
    throw handleApiError(error, 'cancelRemit');
  }
};

export const getContactSend = async (token, value) => {
  try {
    const response = await axios.get(`${apiUrl}/users/search/users/${value}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'getContactSend');
  }
};

export const getAgentsCountries = async (token) => {
  try {
    const response = await axios.get(`${apiUrl}/agent/get-locations`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'getAgentsCountries');
  }
};

export const getUserData = async (token, userID) => {
  try {
    const response = await axios.get(`${apiUrl}/users/userdata/${userID}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'getUserData');
  }
};

// WhatsApp OTP functions
export const getWhatsAppOTP = async (token, number, deviceJwt) => {
  const generatedOtp = Math.floor(100000 + Math.random() * 900000).toString();
  store.dispatch(setWhatsAppCode(generatedOtp));

  try {
    const response = await axios.post(
      `${apiUrl}/auth/generate-whatsapp-code`,
      { otp: generatedOtp, number },
      { headers: { 
        Authorization: `Bearer ${token}`, 
        device: deviceJwt 
      } }
    );
    
    store.dispatch(get_whatsapp_verification_status());
    store.dispatch(initializeSocketConnection());
    return generatedOtp;
  } catch (error) {
    const errorDetails = handleApiError(error, 'getWhatsAppOTP');
    throw new Error(`Failed to generate OTP: ${errorDetails.message}`);
  }
};

export const trustDeviceFunc = async (token, otp) => {
  try {
    const data = await setUserDevice(otp, token).data;
    
    if (data.access_token) {
      store.dispatch(setWhatsAppCodeVerificationStatus(false));
      store.dispatch(setJwtToken(data.access_token));
      return data;
    } else {
      throw new Error(data.message || 'Device trust failed');
    }
  } catch (error) {
    console.error('Error in trustDeviceFunc:', error);
    
    if (error.message.includes('Access token')) {
      toast.error('Failed to authenticate. Please try again.');
    } else {
      toast.error(error.message || 'An unexpected error occurred');
    }
    
    throw error;
  }
};